/**
 * #431 — first-login welcome card.
 *
 * A new staffer used to land on their persona home COLD: the invite email
 * promised "this is where you'll see your grooming queue each morning…"
 * and then the app said nothing at all. This card is the other half of
 * that promise — the SAME sentence, on the screen the email pointed at,
 * exactly once.
 *
 * ONE source of truth for the copy: `ROLE_INVITE_BLURBS` in
 * `@ferro/core/src/rbac/role-invite-copy.js` — the module the invite email
 * renders from. Nothing here re-authors it, and a test pins the rendered
 * card text to that map, so email and card cannot silently drift.
 *
 * Owner: NO card. Same rule as the email (an owner has no blurb — their
 * welcome line already says "invited to manage {tenant}", and the person
 * who set the business up does not need to be told what the app is for).
 * Unknown/legacy role slugs ('staff', 'admin'): no blurb ⇒ no card, rather
 * than inventing duties we don't know they have.
 *
 * PRIMARY role, not the union (#428/#435): additive role grants widen which
 * surfaces an account can REACH, but `personaHomeRoute` still lands the
 * staffer on whatever their PRIMARY role owns. The card must describe the
 * screen they're standing on, so it keys off `staff.role` — a groomer with
 * a receptionist grant lands on /groomer and reads the groomer sentence.
 *
 * Dismissal is SERVER-side (`welcomeDismissedAt` on StaffUser), never
 * localStorage: HTHD's staff share a kiosk tablet and switch browsers, so a
 * device-local flag would re-show a dismissed card on the next device and
 * hide it on a device where the person has never seen it.
 *
 * Design Philosophy principles served:
 *   P2 (the next action is always obvious) — the card TEACHES the screen a
 *     new staffer just landed on; "cold landing with no orientation" is the
 *     dead-end class P2 calls a defect. Its dismiss control is deliberately
 *     NOT a filled-primary: the host view already owns the single primary
 *     (Clock In on the persona homes), and one primary per view is the rule.
 *   P3 (the interface recedes) — one heading, one sentence, one control;
 *     it renders once and then is gone forever. No decoration.
 *   P5 (never lie about data) — a failed dismiss KEEPS the card and says so
 *     inline; we never hide it optimistically and let the server's "no" pass
 *     silently, which would re-show the card on the next login with no
 *     explanation.
 *   P6 (two-layer tokens) — structural `--ferro-*` spacing/radius/elevation,
 *     tenant `--brand-*` color and heading font. No literals.
 *   P7 (built for hands) — ≥44px dismiss target, no hover-only affordance.
 *
 * Five states: default (the card) · loading (renders NOTHING — see below) ·
 * empty (no blurb for the role ⇒ no card, the intended terminal state) ·
 * error (status fetch fails ⇒ no card, an orientation banner must never
 * break a home screen; dismiss fails ⇒ card stays + inline recovery copy) ·
 * stale (N/A — the card reads its own state fresh on mount and reconciles
 * against the server response after the dismiss; it never renders aged data).
 *
 * Why loading renders nothing rather than a skeleton: for everyone but a
 * brand-new staffer this component's answer is "already dismissed, render
 * nothing". A skeleton would flash a placeholder above the day's work on
 * EVERY page load and then collapse — a layout shift for the 99% case to
 * serve the 1%. The card is additive chrome, so it appears when (and only
 * when) the server says it should.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ClientConfig } from '@ferro/config';
import { roleBlurbForSlug } from '@ferro/core/src/rbac/role-invite-copy.js';
import { useAuth } from '../contexts/AuthContext.js';
import { adminStaffWelcomeApi, humanizeApiError } from '../lib/api.js';

export type WelcomeCardProps = {
  config: ClientConfig;
};

type CardState =
  | { kind: 'loading' }
  | { kind: 'hidden' }
  | { kind: 'visible' }
  | { kind: 'dismissing' }
  | { kind: 'dismiss_failed'; message: string };

export function WelcomeCard({ config }: WelcomeCardProps): JSX.Element | null {
  const { staff } = useAuth();
  const apiUrl = config.deployment.apiUrl;

  // PRIMARY role (see header). Owner / unknown slugs have no blurb → no card.
  const blurb = staff?.role ? roleBlurbForSlug(staff.role) : undefined;

  const [state, setState] = useState<CardState>({ kind: 'loading' });

  useEffect(() => {
    // No blurb ⇒ no card ⇒ don't even ask the server.
    if (!blurb) {
      setState({ kind: 'hidden' });
      return;
    }
    let cancelled = false;
    void adminStaffWelcomeApi.get(apiUrl).then((res) => {
      if (cancelled) return;
      // Fail QUIET: an orientation banner must never break a home screen,
      // and a staffer who never sees the card is strictly better off than a
      // staffer staring at an error where their queue should be.
      // `!res.data.welcome` matters as much as `!res.data`: a 200 whose body
      // omits `welcome` used to reach the `.dismissed_at` read below and throw
      // an unhandled rejection out of this effect — the exact "banner breaks
      // the home screen" outcome this block exists to prevent.
      if (res.error || !res.data?.welcome) {
        setState({ kind: 'hidden' });
        return;
      }
      setState(
        res.data.welcome.dismissed_at === null
          ? { kind: 'visible' }
          : { kind: 'hidden' },
      );
    }).catch(() => {
      if (!cancelled) setState({ kind: 'hidden' });
    });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, blurb]);

  const handleDismiss = useCallback(async (): Promise<void> => {
    setState({ kind: 'dismissing' });
    const res = await adminStaffWelcomeApi.dismiss(apiUrl);
    if (res.error || !res.data) {
      // P5: the server did NOT record the dismissal, so the card stays and
      // says so. Hiding it here would be a lie the next login exposes.
      setState({
        kind: 'dismiss_failed',
        message: humanizeApiError(res.error ?? 'Could not save that.'),
      });
      return;
    }
    setState({ kind: 'hidden' });
  }, [apiUrl]);

  if (!blurb) return null;
  if (state.kind === 'loading' || state.kind === 'hidden') return null;

  const busy = state.kind === 'dismissing';

  return (
    <section
      className="ferro-welcome-card"
      role="status"
      aria-labelledby="ferro-welcome-card-title"
      data-testid="welcome-card"
    >
      <div className="ferro-welcome-card__body">
        <h2 id="ferro-welcome-card-title" className="ferro-welcome-card__title">
          {staff?.first_name
            ? `Welcome to ${config.brand.name}, ${staff.first_name}.`
            : `Welcome to ${config.brand.name}.`}
        </h2>
        <p className="ferro-welcome-card__blurb">{blurb}</p>
        {state.kind === 'dismiss_failed' ? (
          <p className="ferro-welcome-card__error" role="alert">
            {state.message} Tap “Got it” again to hide this.
          </p>
        ) : null}
      </div>
      <button
        type="button"
        className="ferro-btn ferro-btn--outline ferro-welcome-card__dismiss"
        onClick={() => void handleDismiss()}
        disabled={busy}
      >
        {busy ? 'Saving…' : 'Got it'}
      </button>
    </section>
  );
}
